import { useEffect, useState } from "react";
import { currentOrgId, salesDocPath } from "../api/client";
import type { ApprovalRequest } from "../api/access";
import type { EntryList, TimeEntry } from "../api/projects";
import type { Installment, Invoice, Onboarding, PayMethod } from "../api/types";
import type { SalesDoc } from "../api/sales-types";
import { t, tx } from "../i18n";
import { fmtMoney } from "../ui/currency/money";
import { PAY_METHODS } from "../ui/labels";
import { cap, fmtDate, fmtDateTime, fmtInr, todayIst } from "../ui/format";
import { Field, Pill } from "../ui/parts";
import { currencyPlaces, shownTotal } from "./edit-core";
import { blockState } from "./numberBlocks";
import { keptLatest, type OfflineProfile } from "./offlineCache";
import { editOrQueue, queueIssue, sendOrQueue, type OutboxItem } from "./outbox";
import { Shell } from "./OfflineForms";

/*
 * Wave 8 (docs/implemented-offline8.md): what the offline screen after a reload with no connection (PwaUi.tsx OfflineApp)
 * offers besides wave 6's new records: the same queued actions as the full CRM opened online, from the copies kept on this
 * device only, in the offline profile's organization:
 *   - edits of a kept customer or vendor (name, email, phone, payment terms) and of a kept draft invoice (date, notes, lines),
 *     sent later with the version they were opened at, exactly as the full screens' edits (outbox.ts editOrQueue);
 *   - issuing a kept draft, or a new invoice, with the next number reserved to this device (outbox.ts queueIssue);
 *   - approvals: the kept requests waiting for this person, and (a role that may approve time) the kept time entries waiting;
 *   - a payment on an onboarding instalment, from a kept onboarding or the kept Billing invoices, with the amount shown as due.
 * What the role may do comes from the profile kept at sign-in (`access`); the server checks everything again on replay.
 */

export type ActionForm =
  | { kind: "edit-party"; what: "customer" | "vendor"; path: string; record: Record<string, unknown>; name: string }
  | { kind: "edit-draft"; doc: SalesDoc }
  | { kind: "issue"; doc: SalesDoc }
  | { kind: "decide"; r: ApprovalRequest; verdict: "approve" | "reject" }
  | { kind: "decide-time"; entries: TimeEntry[]; verdict: "approve" | "reject" }
  | { kind: "pay-installment"; onboarding: string; installment: string; amount: string; label: string; client: string };

/** What the kept profile says the role may do in a module; true when unknown (a profile kept before wave 8). */
export const mayDo = (p: OfflineProfile, module: string, action: string) => !p.access || Boolean(p.access[module]?.includes(action));

/** `queued`: kept on this device (no connection), rather than done on the server now. */
type Props = { onClose: () => void; onQueued: (label: string, queued: boolean) => void };

/** Runs a queue call with the form's busy and error state. */
function useQueue(onQueued: (label: string, queued: boolean) => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      const out = await fn();
      onQueued(label, typeof out === "string" || Boolean((out as { queued?: unknown } | undefined)?.queued));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
}

const blank = (v: unknown) => (v === undefined || v === null || v === "" ? null : v);

export function ActionDrawer({ form, ...p }: Props & { form: ActionForm }) {
  if (form.kind === "edit-party") return <EditPartyForm form={form} {...p} />;
  if (form.kind === "edit-draft") return <EditDraftForm doc={form.doc} {...p} />;
  if (form.kind === "issue") return <IssueForm doc={form.doc} {...p} />;
  if (form.kind === "decide") return <DecideForm r={form.r} verdict={form.verdict} {...p} />;
  if (form.kind === "decide-time") return <DecideTimeForm entries={form.entries} verdict={form.verdict} {...p} />;
  return <PayInstallmentForm form={form} {...p} />;
}

function EditPartyForm({ form, onClose, onQueued }: Props & { form: Extract<ActionForm, { kind: "edit-party" }> }) {
  const r = form.record;
  const nameKey = form.what === "customer" ? "display_name" : "name";
  const [name, setName] = useState(String(r[nameKey] ?? ""));
  const [email, setEmail] = useState(String(r.email ?? ""));
  const [phone, setPhone] = useState(String(r.phone ?? ""));
  const [terms, setTerms] = useState(String(r.payment_terms_days ?? 0));
  const q = useQueue(onQueued);
  function save() {
    if (!name.trim()) return q.setError(t("Name is required."));
    if (!/^\d{1,3}$/.test(terms.trim())) return q.setError(t("Payment terms is a whole number of days, 0 to 365."));
    // Only what changed is sent: a conflict then names only those fields.
    const body: Record<string, unknown> = {};
    if (name.trim() !== r[nameKey]) body[nameKey] = name.trim();
    if (blank(email.trim()) !== blank(r.email)) body.email = email.trim() || null;
    if (blank(phone.trim()) !== blank(r.phone)) body.phone = phone.trim() || null;
    if (Number(terms) !== Number(r.payment_terms_days ?? 0)) body.payment_terms_days = Number(terms);
    if (!Object.keys(body).length) return q.setError(t("Nothing was changed."));
    const label = form.what === "customer" ? t("Edit customer · {name}", { name: form.name }) : t("Edit vendor · {name}", { name: form.name });
    void q.run(label, () => editOrQueue({ label, path: form.path, body, record: r }));
  }
  return (
    <Shell title={t("Edit {name}", { name: form.name })} sub={t("Sent with the version kept on this device. If someone changed it meanwhile, you choose what to keep.")} busy={q.busy} error={q.error} ready onClose={onClose} onSave={save}>
      <Field label={t("Name")}>
        <input value={name} maxLength={160} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label={t("Email")}>
        <input type="email" value={email} maxLength={200} onChange={(e) => setEmail(e.target.value)} />
      </Field>
      <Field label={t("Phone")}>
        <input type="tel" value={phone} maxLength={40} onChange={(e) => setPhone(e.target.value)} />
      </Field>
      <Field label={t("Payment terms (days)")}>
        <input inputMode="numeric" value={terms} onChange={(e) => setTerms(e.target.value)} />
      </Field>
    </Shell>
  );
}

type DraftLine = { item_id: string | null; name: string; description: string | null; hsn_sac: string | null; unit: string | null; quantity: string; rate: string; discount_type: string | null; discount: string | null; tax_rate: number | null };
const lineOf = (l: SalesDoc["lines"][number]): DraftLine => ({ item_id: l.item_id, name: l.name, description: l.description, hsn_sac: l.hsn_sac, unit: l.unit, quantity: String(l.quantity), rate: String(l.rate), discount_type: l.discount_type, discount: l.discount, tax_rate: l.tax_rate_in });

function EditDraftForm({ doc, onClose, onQueued }: Props & { doc: SalesDoc }) {
  const [date, setDate] = useState(doc.issue_date);
  const [notes, setNotes] = useState(doc.notes ?? "");
  const [lines, setLines] = useState<DraftLine[]>(() => doc.lines.map(lineOf));
  const q = useQueue(onQueued);
  const set = (i: number, patch: Partial<DraftLine>) => setLines((ls) => ls.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  function save() {
    if (!lines.length) return q.setError(t("Add at least one line with an item or a name and a quantity."));
    if (lines.some((l) => !(Number(l.quantity) > 0) || !/^\d{1,10}(\.\d{1,2})?$/.test(l.rate.replace(/[,\s₹]/g, "")))) return q.setError(t("Each line needs a quantity above 0 and a rate."));
    const body: Record<string, unknown> = {};
    if (date !== doc.issue_date) body.issue_date = date;
    if (blank(notes.trim()) !== blank(doc.notes)) body.notes = notes.trim() || null;
    const now = lines.map((l) => ({ ...l, rate: l.rate.replace(/[,\s₹]/g, "") }));
    if (JSON.stringify(now) !== JSON.stringify(doc.lines.map(lineOf))) body.lines = now;
    if (!Object.keys(body).length) return q.setError(t("Nothing was changed."));
    const label = t("Edit draft invoice · {customer}", { customer: doc.customer_name });
    void q.run(label, () => editOrQueue({ label, path: salesDocPath("invoice", doc.id), body, record: doc }));
  }
  return (
    <Shell title={t("Edit draft · {customer}", { customer: doc.customer_name })} sub={t("Sent with the version kept on this device. If someone changed it meanwhile, you choose what to keep.")} busy={q.busy} error={q.error} ready onClose={onClose} onSave={save}>
      <Field label={t("Date")}>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>
      {lines.map((l, i) => (
        <fieldset key={i} className="pwa-line">
          <legend>{l.name || t("Line {n}", { n: i + 1 })}</legend>
          <Field label={t("Quantity")}>
            <input inputMode="decimal" value={l.quantity} onChange={(e) => set(i, { quantity: e.target.value })} />
          </Field>
          <Field label={t("Rate (₹, including GST)")}>
            <input inputMode="decimal" value={l.rate} onChange={(e) => set(i, { rate: e.target.value })} />
          </Field>
          {lines.length > 1 && (
            <button type="button" className="wo-link" onClick={() => setLines((ls) => ls.filter((_, k) => k !== i))}>
              {t("Remove this line")}
            </button>
          )}
        </fieldset>
      ))}
      <Field label={t("Customer notes")}>
        <textarea value={notes} rows={2} maxLength={1000} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </Shell>
  );
}

/** The device's block, as the offline screen and the issue form describe it. */
export function useBlock(items: OutboxItem[]) {
  const [b, setB] = useState<Awaited<ReturnType<typeof blockState>> | undefined>(undefined);
  const n = items.length;
  useEffect(() => void blockState(items, currentOrgId() ?? 1).then(setB, () => setB(null)), [n]); // eslint-disable-line react-hooks/exhaustive-deps
  return b;
}

export function BlockNote({ items }: { items: OutboxItem[] }) {
  const b = useBlock(items);
  if (b === undefined) return null;
  const text = !b
    ? t("No invoice numbers are reserved on this device. Go online once so it can reserve a block.")
    : !b.allowed
      ? t("Your role can't issue invoices, so it can't issue them offline either.")
      : b.left
        ? t("{n} invoice numbers reserved to this device can be issued offline, until {when}.", { n: b.left, when: b.until ? fmtDateTime(b.until) : "—" })
        : t("This device's reserved invoice numbers are used up or expired. Go online once so it can reserve a fresh block.");
  return <p className="crm-note">{text}</p>;
}

/** What a draft totals on this device: the server's figure, or worked out again when it was edited offline. */
export function totalOf(doc: SalesDoc) {
  const edited = doc.lines.some((l) => l.amount === "");
  return edited ? shownTotal({ lines: doc.lines as unknown as Record<string, unknown>[], discount_type: doc.discount_type, discount: doc.discount, shipping: doc.shipping, adjustment: doc.adjustment }, doc.currency_decimals ?? currencyPlaces(null)) : doc.total;
}

function IssueForm({ doc, onClose, onQueued }: Props & { doc: SalesDoc }) {
  const q = useQueue(onQueued);
  const total = totalOf(doc);
  const edited = total !== doc.total;
  function save() {
    const tax = edited ? null : (Number(doc.cgst_amount) + Number(doc.sgst_amount) + Number(doc.igst_amount)).toFixed(doc.currency_decimals ?? 2);
    void q.run(t("Issued offline · {customer}", { customer: doc.customer_name }), async () => {
      const number = await queueIssue({ label: doc.customer_name, day: doc.issue_date, invoice: { id: doc.id, updated_at: doc.updated_at }, shown: { total, tax } });
      return number;
    });
  }
  return (
    <Shell title={t("Issue invoice · {customer}", { customer: doc.customer_name })} sub={t("It takes the next invoice number reserved to this device and is sent when the server answers. It is not emailed: email it from the invoice once it is sent.")} busy={q.busy} error={q.error} ready onClose={onClose} onSave={save} action={t("Issue offline")}>
      <p className="crm-note">{t("Total on this device: {total}. The server works the GST out again and tells you if its figures differ.", { total: fmtMoney(total, doc) })}</p>
    </Shell>
  );
}

function DecideForm({ r, verdict, onClose, onQueued }: Props & { r: ApprovalRequest; verdict: "approve" | "reject" }) {
  const [note, setNote] = useState("");
  const q = useQueue(onQueued);
  function save() {
    if (verdict === "reject" && !note.trim()) return q.setError(t("Say why, so the person knows what to change."));
    const label = verdict === "approve" ? t("Approve · {summary}", { summary: r.summary }) : t("Reject · {summary}", { summary: r.summary });
    void q.run(label, () => sendOrQueue({ kind: "approval", label, path: `/admin/approvals/${r.id}/${verdict}`, body: { note: note.trim() || undefined }, version: r.version, record: `/admin/approvals/${r.id}` }));
  }
  return (
    <Shell title={`${verdict === "approve" ? t("Approve") : t("Reject")}: ${r.summary}`} busy={q.busy} error={q.error} ready onClose={onClose} onSave={save} action={verdict === "approve" ? t("Approve") : t("Reject")}>
      <p className="crm-note">
        {t("{kind_label} of {amount}, asked by", { kind_label: tx(r.kind_label), amount: fmtInr(r.amount, true) })} {r.requested_by_name}.
      </p>
      <Field label={verdict === "approve" ? t("Note (optional)") : t("Why")}>
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Shell>
  );
}

function DecideTimeForm({ entries, verdict, onClose, onQueued }: Props & { entries: TimeEntry[]; verdict: "approve" | "reject" }) {
  const [note, setNote] = useState("");
  const q = useQueue(onQueued);
  function save() {
    if (verdict === "reject" && !note.trim()) return q.setError(t("Say why, so the person knows what to change."));
    const label = verdict === "approve" ? t("Approve time · {n}", { n: entries.length }) : t("Reject time · {n}", { n: entries.length });
    void q.run(label, () => sendOrQueue({ kind: "approval", label, path: `/admin/projects/time-entries/${verdict}`, body: { ids: entries.map((e) => e.id), note: verdict === "approve" ? undefined : note.trim(), base_versions: Object.fromEntries(entries.map((e) => [e.id, e.updated_at])) } }));
  }
  return (
    <Shell title={verdict === "approve" ? t("Approve time · {n}", { n: entries.length }) : t("Reject time · {n}", { n: entries.length })} busy={q.busy} error={q.error} ready onClose={onClose} onSave={save} action={verdict === "approve" ? t("Approve") : t("Reject")}>
      <ul className="pwa-list">
        {entries.map((e) => (
          <li key={e.id}>
            <span>
              <strong>{t("{date}: {hours} h · {who}", { date: fmtDate(e.date), hours: e.hours, who: e.staff_name })}</strong>
              <small>
                {e.project_name}
                {e.notes ? ` · ${e.notes}` : ""}
              </small>
            </span>
          </li>
        ))}
      </ul>
      {verdict === "reject" && (
        <Field label={t("Why")}>
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      )}
    </Shell>
  );
}

function PayInstallmentForm({ form, onClose, onQueued }: Props & { form: Extract<ActionForm, { kind: "pay-installment" }> }) {
  const [method, setMethod] = useState<PayMethod>("upi");
  const [reference, setReference] = useState("");
  const [on, setOn] = useState(todayIst());
  const q = useQueue(onQueued);
  function save() {
    const label = t("Payment {amount} on {label} · {customer_name}", { amount: fmtInr(form.amount, true), label: tx(form.label), customer_name: form.client });
    void q.run(label, () => installmentPayment({ ...form, method, reference: reference.trim() || null, received_on: on, label }));
  }
  return (
    <Shell title={t("Record {amount} received", { amount: fmtInr(form.amount, true) })} sub={t("{amount} was due when this copy was saved. The server checks it again when the payment is sent, and emails the invoice then.", { amount: fmtInr(form.amount, true) })} busy={q.busy} error={q.error} ready onClose={onClose} onSave={save}>
      <Field label={t("How did they pay?")}>
        <select value={method} onChange={(e) => setMethod(e.target.value as PayMethod)}>
          {PAY_METHODS.map((m) => (
            <option key={m.value} value={m.value}>
              {tx(m.label)}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t("Reference (UTR, cheque no.)")}>
        <input value={reference} maxLength={120} onChange={(e) => setReference(e.target.value)} />
      </Field>
      <Field label={t("Date received")}>
        <input type="date" value={on} max={todayIst()} onChange={(e) => setOn(e.target.value)} />
      </Field>
    </Shell>
  );
}

/**
 * A payment on an onboarding instalment (an onboarding, or a Billing invoice awaiting payment), online or kept for later:
 * with the amount this device showed as due, so the replay is refused if it changed or was paid meanwhile
 * (server/offline-sync.js). Used by the full Onboarding and Billing screens too.
 */
export function installmentPayment(op: { onboarding: string; installment: string; amount: string; method: PayMethod; reference: string | null; received_on: string | null; label: string }) {
  return sendOrQueue<Onboarding>({
    kind: "payment",
    label: op.label,
    path: `/admin/onboarding/${op.onboarding}/installments/${op.installment}/pay`,
    body: { method: op.method, reference: op.reference, received_on: op.received_on, expected_amount: op.amount },
    record: `/admin/onboarding/${op.onboarding}/installments/${op.installment}`,
  });
}

/** The kept list at a path (any filters), or null; undefined while reading. */
function useLatest<T>(path: string, bump: number) {
  const [v, setV] = useState<T | null | undefined>(undefined);
  useEffect(() => void keptLatest<T>(path).then(setV, () => setV(null)), [path, bump]);
  return v;
}

const waitingFor = (items: OutboxItem[], record: string) => items.some((i) => i.record === record || i.path.startsWith(`${record}/`));

/** Requests waiting for this person, and time waiting for approval, as kept the last time those screens were open. */
export function ApprovalsSection({ profile, items, open }: { profile: OfflineProfile; items: OutboxItem[]; open: (f: ActionForm) => void }) {
  const requests = useLatest<ApprovalRequest[]>("/admin/approvals", items.length);
  const time = useLatest<EntryList>("/admin/projects/time-entries", items.length);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const asks = (requests ?? []).filter((r) => r.status === "pending" && r.can_decide);
  const queuedTime = new Set(items.filter((i) => i.path.startsWith("/admin/projects/time-entries/")).flatMap((i) => ((i.body as { ids?: string[] }).ids ?? [])));
  const entries = mayDo(profile, "projects", "approve") ? (time?.entries ?? []).filter((e) => e.status === "pending" && e.staff_id !== profile.id && !queuedTime.has(e.id)) : [];
  if (!asks.length && !entries.length) return null;
  const chosen = entries.filter((e) => picked.has(e.id));
  return (
    <>
      <h2 className="pwa-offline__title">{t("Waiting for your approval")}</h2>
      <ul className="pwa-list">
        {asks.map((r) => (
          <li key={r.id}>
            <span>
              <strong>{r.summary}</strong>
              <small>
                {tx(r.kind_label)} · {fmtInr(r.amount, true)} · {r.requested_by_name}
              </small>
            </span>
            {waitingFor(items, `/admin/approvals/${r.id}`) ? (
              <Pill tone="amber">{t("Waiting to be sent")}</Pill>
            ) : (
              <span className="pwa-actions">
                <button type="button" className="button button--secondary button--sm" onClick={() => open({ kind: "decide", r, verdict: "reject" })}>
                  {t("Reject")}
                </button>
                <button type="button" className="button button--primary button--sm" onClick={() => open({ kind: "decide", r, verdict: "approve" })}>
                  {t("Approve")}
                </button>
              </span>
            )}
          </li>
        ))}
        {entries.map((e) => (
          <li key={e.id}>
            <label className="crm-check">
              <input type="checkbox" checked={picked.has(e.id)} onChange={() => setPicked((s) => (s.has(e.id) ? new Set([...s].filter((x) => x !== e.id)) : new Set([...s, e.id])))} />{" "}
              <span>
                <strong>{t("{date}: {hours} h · {who}", { date: fmtDate(e.date), hours: e.hours, who: e.staff_name })}</strong>
                <small>{e.project_name}</small>
              </span>
            </label>
          </li>
        ))}
      </ul>
      {entries.length > 0 && (
        <p className="pwa-actions">
          <button type="button" className="button button--secondary button--sm" disabled={!chosen.length} onClick={() => (open({ kind: "decide-time", entries: chosen, verdict: "reject" }), setPicked(new Set()))}>
            {t("Reject time · {n}", { n: chosen.length })}
          </button>
          <button type="button" className="button button--primary button--sm" disabled={!chosen.length} onClick={() => (open({ kind: "decide-time", entries: chosen, verdict: "approve" }), setPicked(new Set()))}>
            {t("Approve time · {n}", { n: chosen.length })}
          </button>
        </p>
      )}
    </>
  );
}

/** Billing invoices awaiting payment (onboarding instalments), as kept the last time Billing was open. */
export function BillingSection({ profile, items, open }: { profile: OfflineProfile; items: OutboxItem[]; open: (f: ActionForm) => void }) {
  const rows = useLatest<Invoice[]>("/admin/billing/invoices", items.length);
  if (!mayDo(profile, "onboarding", "edit")) return null;
  const due = (rows ?? []).filter((r) => !r.source && r.onboarding_id && r.status === "issued");
  if (!due.length) return null;
  return (
    <>
      <h2 className="pwa-offline__title">{t("Billing invoices awaiting payment")}</h2>
      <ul className="pwa-list">
        {due.map((r) => (
          <li key={r.id}>
            <span>
              <strong>{r.tenant_name}</strong>
              <small>
                {fmtInr(r.balance_due, true)} · {t("due {date}", { date: fmtDate(r.due_date) })}
              </small>
            </span>
            {waitingFor(items, `/admin/onboarding/${r.onboarding_id}/installments/${r.id}`) ? (
              <Pill tone="amber">{t("Waiting to be sent")}</Pill>
            ) : (
              <button type="button" className="button button--secondary button--sm" onClick={() => open({ kind: "pay-installment", onboarding: r.onboarding_id ?? "", installment: r.id, amount: r.balance_due, label: r.invoice_number, client: r.tenant_name })}>
                {t("Record payment")}
              </button>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

/** A kept onboarding: its instalments, with Record payment on those still due. */
export function OnboardingView({ o, items, profile, open }: { o: Onboarding; items: OutboxItem[]; profile: OfflineProfile; open: (f: ActionForm) => void }) {
  const may = mayDo(profile, "onboarding", "edit") && o.status !== "cancelled";
  return (
    <ul className="pwa-list">
      {o.installments.map((i: Installment) => (
        <li key={i.id}>
          <span>
            <strong>{tx(i.label)}</strong>
            <small>
              {fmtInr(i.amount, true)} · {tx(cap(i.status.replace(/_/g, " ")))}
              {i.invoice_number ? ` · ${i.invoice_number}` : ""}
            </small>
          </span>
          {i.status !== "paid" && i.status !== "cancelled" && may && (waitingFor(items, `/admin/onboarding/${o.id}/installments/${i.id}`) ? (
            <Pill tone="amber">{t("Waiting to be sent")}</Pill>
          ) : (
            <button type="button" className="button button--secondary button--sm" onClick={() => open({ kind: "pay-installment", onboarding: o.id, installment: i.id, amount: i.amount, label: i.label, client: o.client_name })}>
              {t("Record payment")}
            </button>
          ))}
        </li>
      ))}
    </ul>
  );
}
